// Sopwith 3D - 2D "retro" view.
//
// Copyright (C) 1984-2000 David L. Clark
// Copyright (C) 2001-2026 Simon Howard and SDL Sopwith contributors
// Copyright (C) 2026 Sopwith 3D contributors
//
// This program is free software; you can redistribute it and/or modify it
// under the terms of the GNU General Public License as published by the Free
// Software Foundation; either version 2 of the License, or (at your option)
// any later version. This program is distributed WITHOUT ANY WARRANTY.
//
// Draws the simulation the way the original did: 320x200, four colours,
// the player held at column 152, a status bar with minimap and gauges along
// the bottom. It exists to verify the simulation against the real game side
// by side, and later doubles as the "classic" display mode.

import {
  MAX_Y,
  MAXBOMBS,
  MAXFUEL,
  MAXROUNDS,
  MAXMISSILES,
  MAXBURSTS,
  SCR_CENTR,
  SCR_HGHT,
  SCR_MNSH,
  SCR_WDTH,
} from '../sim/constants';
import { clampRange, inRange } from '../sim/fixed';
import type { Game } from '../sim/game';
import { ObjType } from '../sim/types';
import { Framebuffer, fuselageColor, PALETTES, type Palette } from './framebuffer';

const GAUGEX = SCR_CENTR - 25;

export interface Debug2DOptions {
  palette?: number;
  solidGround?: boolean;
}

/** Renders a Game into a Framebuffer. Pure; no DOM. */
export class ClassicView {
  readonly fb = new Framebuffer();
  solidGround = false;
  /** Left edge of the viewport in world units, after the last render. */
  displx = 0;

  constructor(opts: Debug2DOptions = {}) {
    this.solidGround = opts.solidGround ?? false;
  }

  render(g: Game): void {
    const fb = this.fb;
    fb.clear();
    this.statusBar(g);

    const player = g.consoleplayer;
    this.displx = clampRange(0, player.x - SCR_CENTR, g.level.maxX - SCR_WDTH - 1);
    const displx = this.displx;

    for (let ob = g.world.objtop; ob; ob = ob.next) {
      const sym = ob.symbol;
      if (!ob.drwflg || !sym) continue;
      if (inRange(displx - sym.w, ob.x, displx + SCR_WDTH - 1)) {
        const clr = ob.type === ObjType.SHOT ? 3 : ob.clr;
        fb.dispSymbol(ob.x - displx, ob.y, sym, clr);
      }
    }

    if (this.solidGround) {
      fb.dispGroundSolid(g.ground, displx, 0, SCR_WDTH, SCR_MNSH + 3);
    } else {
      fb.dispGround(g.ground, displx, 0, SCR_WDTH);
    }
  }

  // ---- status bar (swstbar.c) ----

  private statusBar(g: Game): void {
    this.minimap(g);
    this.gauges(g);
  }

  private gauge(x: number, cur: number, max: number, clr: number): void {
    const fb = this.fb;
    const top = Math.min(9, ((cur * 10) / max) | 0) - 1;
    let y = 0;
    for (; y <= top; y++) fb.plotPixel(x, y, clr);
    for (; y <= 9; y++) fb.plotPixel(x, y, 0);
  }

  private gauges(g: Game): void {
    const ob = g.consoleplayer;
    const sep = g.conf.missiles ? 3 : 5;
    let x = GAUGEX;
    this.gauge((x += sep), g.maxcrash - ob.crashcnt, g.maxcrash, 1);
    this.gauge((x += sep), ob.life >> 4, MAXFUEL >> 4, 1);
    this.gauge((x += sep), ob.bombs, MAXBOMBS, 2);
    this.gauge((x += sep), ob.rounds, MAXROUNDS, 3);
    if (g.conf.missiles) {
      this.gauge((x += sep), ob.missiles, MAXMISSILES, 1);
      this.gauge((x += sep), ob.bursts, MAXBURSTS, 2);
    }
  }

  private minimap(g: Game): void {
    const fb = this.fb;
    const maxX = g.level.maxX;
    const rsx = ((maxX / 320) | 0) * 2 + 1;
    const rsy = ((MAX_Y / SCR_MNSH) | 0) + 1;

    // Terrain silhouette, one column per rsx world columns.
    let dx = 0;
    let sx = SCR_CENTR;
    let maxh = 0;
    let y = 0;
    for (let x = 0; x < maxX; x++) {
      maxh = Math.max(maxh, g.ground[x]);
      if (++dx === rsx) {
        maxh = (maxh / rsy) | 0;
        if (maxh === y) {
          fb.plotPixel(sx, maxh, 3);
        } else if (maxh > y) {
          for (++y; y <= maxh; ++y) fb.plotPixel(sx, y, 3);
        } else {
          for (--y; y >= maxh; --y) fb.plotPixel(sx, y, 3);
        }
        y = maxh;
        fb.plotPixel(sx, 0, 3);
        sx++;
        dx = maxh = 0;
      }
    }
    const border = (MAX_Y / rsy) | 0;
    for (let yy = 0; yy <= border; yy++) {
      fb.plotPixel(SCR_CENTR, yy, 3);
      fb.plotPixel(sx, yy, 3);
    }

    // Objects as single dots.
    for (let ob = g.world.objtop; ob; ob = ob.next) {
      const sym = ob.symbol;
      if (!ob.onmap || !sym || ob.x < 0 || ob.x >= maxX) continue;
      const mapx = ob.x + ((sym.w / 2) | 0);
      const groundy = g.ground[ob.x];
      const mapy = ob.y - sym.h <= groundy ? ob.y - sym.h + 8 : ob.y - ((sym.h / 2) | 0);
      const px = SCR_CENTR + ((mapx / rsx) | 0);
      const py = (mapy / rsy) | 0;
      if (py < SCR_MNSH - 1) {
        fb.plotPixel(px, py, fuselageColor(ob.clr));
      }
    }

    // Line separating the status bar from the world.
    for (let x = 0; x < SCR_WDTH; x++) fb.plotPixel(x, SCR_MNSH + 2, 3);
  }
}

/** Presents a ClassicView on a canvas, integer-scaled to fit its container. */
export class ClassicCanvas {
  readonly view: ClassicView;
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly image: ImageData;
  palette: Palette;

  constructor(container: HTMLElement, opts: Debug2DOptions = {}) {
    this.view = new ClassicView(opts);
    this.palette = PALETTES[opts.palette ?? 0];
    this.canvas = document.createElement('canvas');
    this.canvas.width = SCR_WDTH;
    this.canvas.height = SCR_HGHT;
    this.canvas.style.imageRendering = 'pixelated';
    this.canvas.style.display = 'block';
    this.canvas.style.margin = 'auto';
    this.canvas.style.background = '#000';
    container.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d')!;
    this.image = this.ctx.createImageData(SCR_WDTH, SCR_HGHT);
    this.fit(container);
    new ResizeObserver(() => this.fit(container)).observe(container);
  }

  /** Scale by the largest integer factor that fits, keeping 4:3 pixels 1:1. */
  private fit(container: HTMLElement): void {
    const scale = Math.max(
      1,
      Math.floor(Math.min(container.clientWidth / SCR_WDTH, container.clientHeight / SCR_HGHT)),
    );
    this.canvas.style.width = `${SCR_WDTH * scale}px`;
    this.canvas.style.height = `${SCR_HGHT * scale}px`;
    this.canvas.style.marginTop = `${Math.max(0, (container.clientHeight - SCR_HGHT * scale) / 2)}px`;
  }

  render(g: Game): void {
    this.view.render(g);
    this.view.fb.toRGBA(this.image.data, this.palette);
    this.ctx.putImageData(this.image, 0, 0);

    // Score, drawn with a canvas font until the bitmap font is ported.
    const p = g.consoleplayer;
    const [r, gg, b] = this.palette.colors[fuselageColor(p.clr)];
    this.ctx.fillStyle = `rgb(${r},${gg},${b})`;
    this.ctx.font = '8px monospace';
    this.ctx.textBaseline = 'top';
    this.ctx.fillText(String(p.score.score), 8, SCR_HGHT - 24);
  }
}
